import React, { memo, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import type { CostBlock } from '../../../types/backend';
import BlockShell from './BlockShell';
import { chartPalette, formatCurrency } from './blockFormat';

interface Props {
  block: CostBlock;
}

const CostBlockView: React.FC<Props> = memo(({ block }) => {
  const theme = useTheme();
  const colors = chartPalette(theme);
  const period = block.period ?? 'month';

  const { sorted, total } = useMemo(() => {
    const s = [...block.items].sort((a, b) => b.value - a.value);
    const t = block.total > 0 ? block.total : s.reduce((sum, i) => sum + i.value, 0);
    return { sorted: s, total: t };
  }, [block.items, block.total]);

  return (
    <BlockShell bodyPadding={0}>
      <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {formatCurrency(total, block.currency)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            / {period}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {sorted.map((item, i) => {
            const pct = total > 0 ? (item.value / total) * 100 : 0;
            const color = colors[i % colors.length];
            return (
              <Box key={i}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.35 }}>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {item.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'baseline' }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {pct.toFixed(1)}%
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 64,
                        textAlign: 'right',
                      }}
                    >
                      {formatCurrency(item.value, block.currency)}
                    </Typography>
                    {item.period && item.period !== period && (
                      <Typography variant="caption" color="text.secondary">
                        /{item.period}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  role="presentation"
                  sx={{ height: 6, borderRadius: 1, bgcolor: 'action.hover', overflow: 'hidden' }}
                >
                  <Box
                    sx={{
                      height: '100%',
                      width: `${pct}%`,
                      bgcolor: color,
                      borderRadius: 1,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ px: 2, py: 0.75, mt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          {block.items.length} {block.items.length === 1 ? 'item' : 'items'}
        </Typography>
      </Box>
    </BlockShell>
  );
});

CostBlockView.displayName = 'CostBlockView';
export default CostBlockView;
